import { useCallback } from 'react';
import { ChangeTierDialog } from './ChangeTierDialog';
import { PublicAccessDialog } from './PublicAccessDialog';
import { AccessPoliciesDialog } from './AccessPoliciesDialog';
import { GenerateSasDialog } from './GenerateSasDialog';
import { StoragePropertiesPanel } from '../StoragePropertiesPanel';
import { useSasMintedEvents } from '../hooks/useSasMintedEvents';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { QueuedDialogNotice } from './QueuedDialogNotice';
import { useStorageDialogStore, type StorageDialog, type StorageDialogGuard } from './storageDialogStore';
import { HnsHostedDialog } from '../../hns/dialogs/HnsDialogHost';
import { isHnsDialogKind } from '../../hns/dialogs/hnsDialogTypes';

type HostedDialogProps = { dialog: StorageDialog; readOnly: boolean; close: () => void };

function HostedDialog({ dialog, readOnly, close }: HostedDialogProps) {
  // Fable F8: the dialog reports its draft / save state, so the slot never replaces it silently.
  const reportGuard = useStorageDialogStore((state) => state.reportGuard);
  const report = useCallback((guard: StorageDialogGuard) => reportGuard(dialog.instanceId, guard), [reportGuard, dialog.instanceId]);
  if (isHnsDialogKind(dialog.kind)) return <HnsHostedDialog dialog={dialog} readOnly={readOnly} close={close} onGuardChange={report} />; // S8
  if (dialog.kind === 'tier' && dialog.resource.kind === 'blob') {
    const source = dialog.source === 'panel' || dialog.source === 'chip' ? dialog.source : 'row-menu';
    return <ChangeTierDialog connectionId={dialog.connectionId} resource={dialog.resource} source={source} readOnly={readOnly} onAcknowledged={dialog.onAcknowledged} onGuardChange={report} onClose={close} />;
  }
  if (dialog.kind === 'public-access' && dialog.resource.kind === 'container') {
    return <PublicAccessDialog connectionId={dialog.connectionId} resource={dialog.resource} onGuardChange={report} onClose={close} />;
  }
  if (dialog.kind === 'access-policies' && (dialog.resource.kind === 'container' || dialog.resource.kind === 'table' || dialog.resource.kind === 'queue' || dialog.resource.kind === 'share')) {
    return <AccessPoliciesDialog connectionId={dialog.connectionId} resource={dialog.resource} readOnly={readOnly} protocol={dialog.protocol} onGuardChange={report} onClose={close} />;
  }
  if (dialog.kind === 'sas') {   // every target, incl. a share / file (S7); the dialog refuses the ones without a SAS
    const source = dialog.source === 'row-menu' || dialog.source === 'tree' || dialog.source === 'mcp' ? dialog.source : 'toolbar';
    return <GenerateSasDialog connectionId={dialog.connectionId} resource={dialog.resource} source={source} readOnly={readOnly} initialGrant={dialog.grant} onClose={close} />;
  }
  if (dialog.kind === 'properties' && (dialog.resource.kind === 'blob' || dialog.resource.kind === 'container')) {
    // Review E1: the panel reports a save in flight apart from its draft — inputs edited back to the loaded values while
    // a save is pending leave no draft, but that write must still hold the slot (never replaced, never cancelled).
    return <div className="fixed inset-y-0 right-0 z-50 flex flex-col bg-background pt-10 shadow-xl"><QueuedDialogNotice /><StoragePropertiesPanel connectionId={dialog.connectionId} resource={dialog.resource} readOnly={readOnly} onClose={close} onGuardChange={report} /></div>;
  }
  return null;
}

/**
 * App-level mount of the S3 dialogs. Keyed by the opening's `instanceId` (review D RD2): every opening mounts fresh, and
 * the dialog it replaces unmounts — its request hooks cancel whatever it still had in flight. Only a CLEAN dialog is
 * ever replaced: one with a draft or a save in flight keeps the slot and the opening waits (Fable F8, the store).
 */
export function StorageDialogHost() {
  useSasMintedEvents();
  const dialog = useStorageDialogStore((state) => state.dialog);
  const close = useStorageDialogStore((state) => state.closeDialog);
  const readOnly = useConnectionStore((state) => {
    if (!dialog) return false;
    return !!(state.connections.find((connection) => connection.id === dialog.connectionId)?.readOnly
      ?? state.activeConnections.find((connection) => connection.id === dialog.connectionId)?.connection.readOnly);
  });
  return dialog ? <HostedDialog key={dialog.instanceId} dialog={dialog} readOnly={readOnly} close={close} /> : null;
}
